// 좁은 배치(한 화면씩) 여부 — resize·회전·사용자 설정 변경을 실시간 반영.
// Capacitor (Android/iOS) 환경에서도 동일하게 동작 — webview viewport 가 동일 API.
//
// 🖥 2026-08-26 사용자: 포터블 모니터를 세로로 세우면 1080×1920 이 되어 UI 가 온전히 표시되지 않는다.
//   "자동 전환이 아니라 사용자가 골라 전환하는 게 오류가 없을 것" → 두 가지를 함께 둔다.
//   ① 사용자가 고른 배치(설정 > 화면 > 화면 배치)가 있으면 그것이 정본 — 자동 판정보다 우선한다.
//      세로 모니터·듀얼 모니터처럼 크기만으로 맞히기 어려운 환경을 사용자가 확정할 수 있게.
//   ② 자동일 때의 판정: 종전 폭 767 이하에 더해 **세로가 더 긴 1200 이하 화면**도 좁은 배치로 본다
//      (세로 모니터·태블릿 세로). 가로가 더 긴 일반 데스크톱은 종전과 완전히 동일하다.

import { useEffect, useState } from 'react';
import { getLayoutMode, LAYOUT_CHANGE_EVENT } from '../api/prefs';

const MOBILE_QUERY = '(max-width: 767px), (max-width: 1200px) and (orientation: portrait)';

function compute(): boolean {
	const mode = getLayoutMode();
	if (mode === 'narrow') return true;
	if (mode === 'wide') return false;
	if (typeof window === 'undefined' || !window.matchMedia) return false;
	return window.matchMedia(MOBILE_QUERY).matches;
}

// 훅 밖(빈 deps effect 의 이벤트 리스너 등)에서 호출 시점 판정이 필요할 때 — useIsMobile 과 동일 기준 (2026-08-31)
export function isNarrowLayout(): boolean {
	return compute();
}

export function useIsMobile(): boolean {
	const [isMobile, setIsMobile] = useState<boolean>(compute);

	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return;
		const sync = () => setIsMobile(compute());
		const mql = window.matchMedia(MOBILE_QUERY);
		// addEventListener — 구형 Safari 는 addListener 대체
		if (typeof mql.addEventListener === 'function') mql.addEventListener('change', sync);
		else mql.addListener(sync);
		// 사용자가 배치를 바꾸면 창 크기가 그대로여도 다시 계산해야 한다.
		window.addEventListener(LAYOUT_CHANGE_EVENT, sync);
		return () => {
			if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', sync);
			else mql.removeListener(sync);
			window.removeEventListener(LAYOUT_CHANGE_EVENT, sync);
		};
	}, []);

	return isMobile;
}

// 뷰포트 너비(px) — resize 실시간 반영. 패널 단계별 자동 접기(좌→우)·최소폭 판단용.
export function useViewportWidth(): number {
	const [w, setW] = useState<number>(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const handler = () => setW(window.innerWidth);
		window.addEventListener('resize', handler);
		return () => window.removeEventListener('resize', handler);
	}, []);
	return w;
}
